import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import type { Comment } from '@/lib/supabase'

export interface ModerationStats {
  total: number
  aprovados: number
  ocultos: number
  denunciados: number
  pendentes: number
}

export function useModerationStats() {
  const [stats, setStats] = useState<ModerationStats>({ total: 0, aprovados: 0, ocultos: 0, denunciados: 0, pendentes: 0 })
  const [loading, setLoading] = useState(true)

  const fetchStats = useCallback(async () => {
    const { data, error } = await supabase
      .from('comments')
      .select('status')
    
    if (error) {
      console.error('Error fetching stats:', error)
      return
    }

    const counts: ModerationStats = {
      total: data?.length || 0,
      aprovados: 0,
      ocultos: 0,
      denunciados: 0,
      pendentes: 0,
    }
    
    data?.forEach((row) => {
      const status = row.status as string
      if (status === 'aprovado') counts.aprovados++
      else if (status === 'oculto') counts.ocultos++
      else if (status === 'denunciado') counts.denunciados++
      else if (status === 'pendente') counts.pendentes++
    })
    
    setStats(counts)
    setLoading(false)
  }, [])

  useEffect(() => {
    fetchStats()
  }, [fetchStats])

  return { stats, loading, refetch: fetchStats }
}

export function useModerationComments(filter: {
  status?: string
  search?: string
  page?: number
  perPage?: number
}) {
  const [comments, setComments] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [totalCount, setTotalCount] = useState(0)

  const fetchComments = useCallback(async () => {
    setLoading(true)
    
    let query = supabase
      .from('comments')
      .select('*', { count: 'exact' })
    
    if (filter.status && filter.status !== 'todos') {
      query = query.eq('status', filter.status)
    }
    
    if (filter.search) {
      query = query.ilike('content', `%${filter.search}%`)
    }
    
    query = query.order('created_at', { ascending: false })
    
    if (filter.perPage && filter.page) {
      const from = (filter.page - 1) * filter.perPage
      query = query.range(from, from + filter.perPage - 1)
    }
    
    const { data, error, count } = await query
    
    if (error) {
      console.error('Error fetching comments:', error)
    } else {
      setComments(data || [])
      setTotalCount(count || 0)
    }
    setLoading(false)
  }, [filter.status, filter.search, filter.page, filter.perPage])

  useEffect(() => {
    fetchComments()
  }, [fetchComments])

  async function updateStatus(commentId: string, status: string) {
    const { error } = await supabase
      .from('comments')
      .update({ status })
      .eq('id', commentId)
    
    if (error) {
      console.error('Error updating comment:', error)
      return false
    }
    
    await fetchComments()
    return true
  }

  async function deleteComment(commentId: string) {
    const { error } = await supabase
      .from('comments')
      .delete()
      .eq('id', commentId)
    
    if (error) {
      console.error('Error deleting comment:', error)
      return false
    }
    
    await fetchComments()
    return true
  }

  async function approveAll(commentIds: string[]) {
    const { error } = await supabase
      .from('comments')
      .update({ status: 'aprovado' })
      .in('id', commentIds)
    
    if (error) {
      console.error('Error approving comments:', error)
      return false
    }
    
    await fetchComments()
    return true
  }

  async function deleteAll(commentIds: string[]) {
    const { error } = await supabase
      .from('comments')
      .delete()
      .in('id', commentIds)
    
    if (error) {
      console.error('Error deleting comments:', error)
      return false
    }
    
    await fetchComments()
    return true
  }

  return { comments, loading, totalCount, refetch: fetchComments, updateStatus, deleteComment, approveAll, deleteAll }
}